import { Badge, Button, Group, Loader, Select, Text } from '@mantine/core';
import {
    CaretDown, CaretRight,
    CheckCircle, Circle,
    Warning, XCircle,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
    createSaleOrdersForApprovedPlan,
    getPlanShipments,
    getWmsChannelNames,
    previewSaleOrderCreation,
    type PlanShipment,
    type SaleOrderCapacityPreview,
} from '@api/distribution';
import { neutralColors, semanticColors } from '@styles/design-system';
import { showError } from '@utils/notifications';
import { reportWmsPushOutcome } from '@utils/wmsPushOutcome';
import { formatCount } from '@utils/currency';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Phase state machine:
 *   idle → reviewing → diff → creating → done
 *
 * No inventory refresh before the push: the review reads the stock we hold now and only FLAGS
 * lines above it — every line is pushed at its approved quantity. The backend queues a scoped
 * inventory refresh once the sale orders are in the WMS.
 */
type Phase = 'idle' | 'reviewing' | 'diff' | 'creating' | 'done';
type StepKey = 'review' | 'create';
type StepState = 'pending' | 'active' | 'done' | 'error';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_DEFS: Array<{ key: StepKey; label: string }> = [
    { key: 'review',  label: 'Review quantities' },
    { key: 'create',  label: 'Create sale orders' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

function getStepState(phase: Phase, key: StepKey, hasError: boolean): StepState {
    if (hasError && key === 'review') return 'error';
    switch (key) {
        case 'review':
            if (phase === 'idle') return 'pending';
            if (phase === 'reviewing') return 'active';
            return 'done';
        case 'create':
            if (phase === 'idle' || phase === 'reviewing' || phase === 'diff') return 'pending';
            if (phase === 'creating') return 'active';
            return 'done';
    }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepIcon({ state }: { state: StepState }) {
    const base: React.CSSProperties = {
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    };
    if (state === 'done') return (
        <div style={{ ...base, backgroundColor: semanticColors.positive.primary }}>
            <CheckCircle size={15} color="#fff" weight="fill" />
        </div>
    );
    if (state === 'active') return (
        <div style={{ ...base, backgroundColor: 'rgba(109,40,217,0.1)' }}>
            <Loader size={13} color="violet" />
        </div>
    );
    if (state === 'error') return (
        <div style={{ ...base, backgroundColor: 'rgba(239,68,68,0.08)' }}>
            <XCircle size={15} color={semanticColors.critical.primary} weight="fill" />
        </div>
    );
    return (
        <div style={{ ...base, backgroundColor: 'rgba(0,0,0,0.06)' }}>
            <Circle size={13} color="rgba(0,0,0,0.25)" />
        </div>
    );
}

function VerticalStepper({
    phase, hasError,
    descriptions,
}: {
    phase: Phase;
    hasError: boolean;
    descriptions: Record<StepKey, string | null>;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {STEP_DEFS.map((step, idx) => {
                const state = getStepState(phase, step.key, hasError);
                const desc  = descriptions[step.key];
                const isLast = idx === STEP_DEFS.length - 1;

                return (
                    <div key={step.key} style={{ display: 'flex', gap: 12 }}>
                        {/* Icon + vertical connector */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <StepIcon state={state} />
                            {!isLast && (
                                <div style={{
                                    width: 2, flexGrow: 1, minHeight: 18, margin: '4px 0',
                                    backgroundColor: state === 'done'
                                        ? semanticColors.positive.primary
                                        : 'rgba(0,0,0,0.1)',
                                    transition: 'background-color 0.4s ease',
                                }} />
                            )}
                        </div>

                        {/* Label + description */}
                        <div style={{ flex: 1, paddingBottom: isLast ? 0 : 20, paddingTop: 4 }}>
                            <Text style={{
                                fontSize: 14, lineHeight: 1.2,
                                fontWeight: state === 'pending' ? 400 : 600,
                                color: state === 'pending'
                                    ? 'rgba(0,0,0,0.3)'
                                    : state === 'error'
                                    ? semanticColors.critical.primary
                                    : neutralColors.textPrimary,
                            }}>
                                {step.label}
                            </Text>
                            {desc && (
                                <Text style={{
                                    fontSize: 12, marginTop: 3,
                                    color: state === 'active'
                                        ? neutralColors.textSecondary
                                        : 'rgba(0,0,0,0.45)',
                                    fontStyle: state === 'active' ? 'italic' : 'normal',
                                }}>
                                    {desc}
                                </Text>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
    const pct   = total > 0 ? (current / total) * 100 : 0;
    const isDone = current === total && total > 0;
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Text style={{ fontSize: 12, color: neutralColors.textSecondary }}>
                    {isDone ? 'All sale orders created in WMS' : 'Creating sale orders in WMS...'}
                </Text>
                <Text style={{ fontSize: 13, fontWeight: 700, color: neutralColors.textPrimary, fontFamily: 'monospace' }}>
                    {current} / {total}
                </Text>
            </div>
            <div style={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{
                    height: '100%', borderRadius: 3,
                    width: `${pct}%`,
                    background: isDone
                        ? semanticColors.positive.primary
                        : 'linear-gradient(90deg, #6d28d9 0%, #8b5cf6 100%)',
                    transition: 'width 0.5s ease',
                }} />
            </div>
        </div>
    );
}

function DiffSection({ preview }: { preview: SaleOrderCapacityPreview }) {
    const over = preview.lines.filter(l => l.exceedsAvailable);

    if (over.length === 0) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', borderRadius: 8,
                backgroundColor: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
            }}>
                <CheckCircle size={15} color={semanticColors.positive.primary} weight="fill" />
                <Text style={{ fontSize: 13, color: neutralColors.textPrimary }}>
                    All {preview.lines.length} lines are within the stock available at their source.
                </Text>
            </div>
        );
    }

    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '10px 14px', borderRadius: 8, marginBottom: 10,
                backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
            }}>
                <Warning size={15} color={semanticColors.critical.primary} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                <Text style={{ fontSize: 13, color: neutralColors.textPrimary }}>
                    <strong>{over.length} line{over.length !== 1 ? 's' : ''}</strong> exceed the stock available at the source.
                    They will be pushed at the quantities shown — nothing is reduced.
                    {preview.maxUtilPct !== null && (
                        <span style={{ color: neutralColors.textMuted }}>
                            {' '}(available = current stock × {Math.round(preview.maxUtilPct * 100)}% outbound utilisation)
                        </span>
                    )}
                </Text>
            </div>

            <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, overflow: 'hidden' }}>
                {/* Table header */}
                <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 110px 80px 80px',
                    padding: '6px 12px', backgroundColor: 'rgba(0,0,0,0.03)',
                    borderBottom: '1px solid rgba(0,0,0,0.07)',
                }}>
                    {['Product', 'From WH', 'Sending', 'Available'].map(h => (
                        <Text key={h} style={{ fontSize: 10, fontWeight: 700, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {h}
                        </Text>
                    ))}
                </div>

                {/* Rows */}
                <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {over.map((line, i) => (
                        <div key={line.transferId} style={{
                            display: 'grid', gridTemplateColumns: '1fr 110px 80px 80px',
                            padding: '7px 12px', alignItems: 'center',
                            backgroundColor: 'rgba(239,68,68,0.03)',
                            borderBottom: i < over.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                        }}>
                            <Text style={{ fontSize: 12, color: neutralColors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {line.productName ?? line.productId}
                            </Text>
                            <Text style={{ fontSize: 11, color: neutralColors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {line.fromWarehouseName ?? line.fromWarehouseId}
                            </Text>
                            <Text style={{ fontSize: 12, fontWeight: 700, color: semanticColors.critical.primary }}>
                                {formatCount(line.currentQty)}
                            </Text>
                            <Text style={{ fontSize: 12, color: neutralColors.textSecondary }}>
                                {formatCount(line.sendableQty)}
                            </Text>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function ShipmentStatusBadge({
    wmsStatus, saleOrderStatus, saleOrderIsLive, isActive,
}: {
    wmsStatus: PlanShipment['wmsStatus'];
    saleOrderStatus: string | null;
    saleOrderIsLive: boolean;
    isActive: boolean;
}) {
    if (wmsStatus === 'CREATED') {
        // Once in the WMS, show the live sale-order status synced back. The backend classifies
        // dead (Cancelled/Returned/Expired) via the shared status-mapping layer — no FE duplication.
        if (!saleOrderIsLive) return (
            <Group gap={4} align="center" wrap="nowrap">
                <XCircle size={13} color={semanticColors.critical.primary} weight="fill" />
                <Text style={{ fontSize: 11, color: semanticColors.critical.primary, fontWeight: 600 }}>{saleOrderStatus ?? 'Cancelled'}</Text>
            </Group>
        );
        return (
            <Group gap={4} align="center" wrap="nowrap">
                <CheckCircle size={13} color={semanticColors.positive.primary} weight="fill" />
                <Text style={{ fontSize: 11, color: semanticColors.positive.primary, fontWeight: 600 }}>{saleOrderStatus ?? 'In WMS'}</Text>
            </Group>
        );
    }
    if (wmsStatus === 'FAILED') return (
        <Group gap={4} align="center" wrap="nowrap">
            <XCircle size={13} color={semanticColors.critical.primary} weight="fill" />
            <Text style={{ fontSize: 11, color: semanticColors.critical.primary, fontWeight: 600 }}>Failed</Text>
        </Group>
    );
    if (isActive) return <Loader size={12} />;
    return <Badge size="xs" variant="light" color="violet">Pending WMS</Badge>;
}

function ShipmentList({
    shipments, channelKey, pushingCodes, expandedCodes, onToggleExpand, showStatus,
}: {
    shipments: PlanShipment[];
    channelKey: string;
    pushingCodes: Set<string>;
    expandedCodes: Set<string>;
    onToggleExpand: (code: string) => void;
    showStatus: boolean;
}) {
    if (shipments.length === 0) return (
        <Text style={{ fontSize: 13, color: neutralColors.textMuted, textAlign: 'center', padding: 24 }}>
            No shipments found for this plan.
        </Text>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {shipments.map((s, idx) => {
                const isExpanded = expandedCodes.has(s.saleOrderCode);
                const seq        = String(idx + 1).padStart(2, '0');
                const isPushing  = showStatus && pushingCodes.has(s.saleOrderCode) && s.wmsStatus === null;

                return (
                    <div key={s.saleOrderCode} style={{
                        border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8, overflow: 'hidden',
                        backgroundColor: s.wmsStatus === 'CREATED'
                            ? 'rgba(34,197,94,0.03)'
                            : s.wmsStatus === 'FAILED'
                            ? 'rgba(239,68,68,0.03)'
                            : '#fff',
                    }}>
                        {/* Row header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px' }}>
                            <div style={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: 6,
                                backgroundColor: 'rgba(109,40,217,0.08)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Text style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9', fontFamily: 'monospace' }}>
                                    {seq}
                                </Text>
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Text style={{ fontSize: 13, fontWeight: 600, color: neutralColors.textPrimary }}>
                                    {s.saleOrderName ?? s.saleOrderCode}
                                </Text>
                                <Text style={{ fontSize: 10, color: neutralColors.textMuted, fontFamily: 'monospace' }}>
                                    {s.saleOrderCode}
                                    {s.wmsExternalId && (
                                        <span style={{ color: neutralColors.textSecondary }}> · WMS: {s.wmsExternalId}</span>
                                    )}
                                </Text>
                            </div>

                            <Text style={{ fontSize: 11, color: neutralColors.textMuted, flexShrink: 0, whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {(s.fromWarehouseName ?? s.fromWarehouseId)} → {s.toWarehouseName ?? s.toWarehouseId ?? channelKey}
                            </Text>

                            <Text style={{ fontSize: 11, color: neutralColors.textMuted, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                {s.lines.length} SKU{s.lines.length !== 1 ? 's' : ''} · {formatUnits(s.totalQty)}
                            </Text>

                            {showStatus && (
                                <ShipmentStatusBadge wmsStatus={s.wmsStatus} saleOrderStatus={s.saleOrderStatus} saleOrderIsLive={s.saleOrderIsLive} isActive={isPushing} />
                            )}

                            <button
                                type="button"
                                onClick={() => onToggleExpand(s.saleOrderCode)}
                                style={{
                                    flexShrink: 0, padding: 4, borderRadius: 4, border: 'none',
                                    background: 'none', cursor: 'pointer', color: neutralColors.textMuted,
                                    display: 'flex', alignItems: 'center',
                                }}
                            >
                                {isExpanded ? <CaretDown size={13} /> : <CaretRight size={13} />}
                            </button>
                        </div>

                        {/* Error message */}
                        {s.wmsStatus === 'FAILED' && s.wmsErrorMessage && (
                            <div style={{ padding: '0 12px 10px 48px' }}>
                                <Text style={{ fontSize: 11, color: semanticColors.critical.primary }}>
                                    {s.wmsErrorMessage}
                                </Text>
                            </div>
                        )}

                        {/* Expanded SKU detail */}
                        {isExpanded && s.lines.length > 0 && (
                            <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', backgroundColor: 'rgba(0,0,0,0.015)' }}>
                                <div style={{ padding: '6px 12px 6px 48px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 4 }}>
                                        <Text style={{ fontSize: 10, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product</Text>
                                        <Text style={{ fontSize: 10, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Qty</Text>
                                    </div>
                                    {s.lines.slice(0, 20).map(line => (
                                        <div key={line.productId} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '3px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                            <div>
                                                <Text style={{ fontSize: 12, color: neutralColors.textPrimary, lineHeight: 1.3 }}>{line.productName ?? line.productId}</Text>
                                                <Text style={{ fontSize: 10, color: neutralColors.textMuted }}>{line.productId}</Text>
                                            </div>
                                            <Text style={{ fontSize: 12, fontWeight: 600, color: neutralColors.textPrimary, whiteSpace: 'nowrap' }}>
                                                {formatCount(line.qty)}
                                            </Text>
                                        </div>
                                    ))}
                                    {s.lines.length > 20 && (
                                        <Text style={{ fontSize: 11, color: neutralColors.textMuted, paddingTop: 6 }}>
                                            +{s.lines.length - 20} more
                                        </Text>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
    planId: string;
    channelKey: string;
    opened: boolean;
    onClose: () => void;
    onCreated: () => void;
}

export function CreateSaleOrdersModal({ planId, channelKey, opened, onClose, onCreated }: Props) {
    // ── Data state ───────────────────────────────────────────────────────────
    const [shipments,          setShipments]          = useState<PlanShipment[]>([]);
    const [loading,            setLoading]             = useState(false);
    const [wmsType,            setWmsType]             = useState<string | null>(null);
    const [wmsChannelNames,    setWmsChannelNames]     = useState<string[]>([]);
    const [selectedChannelName,setSelectedChannelName] = useState<string | null>(null);
    const [expandedCodes,      setExpandedCodes]       = useState<Set<string>>(new Set());

    // ── Process state ────────────────────────────────────────────────────────
    const [phase,       setPhase]       = useState<Phase>('idle');
    const [preview,     setPreview]     = useState<SaleOrderCapacityPreview | null>(null);
    const [reviewError, setReviewError] = useState<string | null>(null);
    const [pushingCodes,setPushingCodes]= useState<Set<string>>(new Set());

    // ── Polling refs ─────────────────────────────────────────────────────────
    const shipmentPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopAllPolls = () => {
        if (shipmentPollRef.current) { clearInterval(shipmentPollRef.current); shipmentPollRef.current = null; }
    };

    // ── Load on open, reset on close ─────────────────────────────────────────
    useEffect(() => {
        if (!opened) {
            stopAllPolls();
            setPhase('idle');
            setPreview(null);
            setReviewError(null);
            setPushingCodes(new Set());
            return;
        }

        setLoading(true);
        Promise.all([getPlanShipments(planId), getWmsChannelNames(channelKey)])
            .then(([loaded, wms]) => {
                setShipments(loaded);
                setWmsType(wms.wmsType);
                setWmsChannelNames(wms.channelNames);
                if (wms.channelNames.length === 1) setSelectedChannelName(wms.channelNames[0]);
            })
            .catch(() => showError('Failed to load shipments'))
            .finally(() => setLoading(false));

        return () => stopAllPolls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, planId, channelKey]);

    // ── Phase 1: review quantities against the stock we hold now ─────────────
    const handleStartCreation = async () => {
        if (wmsChannelNames.length > 0 && !selectedChannelName) {
            showError('Please select a WMS channel first');
            return;
        }
        setReviewError(null);
        setPhase('reviewing');
        try {
            const p = await previewSaleOrderCreation(planId);
            setPreview(p);
            setPhase('diff');
        } catch {
            setReviewError('Failed to check quantities against stock. Please try again.');
        }
    };

    // ── Phase 2: confirm → create sale orders ─────────────────────────────────
    const handleConfirmCreate = () => {
        const codes = new Set(
            shipments
                .filter(s => s.wmsStatus === null || s.wmsStatus === 'FAILED')
                .map(s => s.saleOrderCode),
        );
        setPushingCodes(codes);
        setPhase('creating');

        // Poll shipments every 1.5 s so rows light up as SOs get created
        shipmentPollRef.current = setInterval(() => {
            getPlanShipments(planId)
                .then(updated => setShipments(updated))
                .catch(() => {});
        }, 1_500);

        createSaleOrdersForApprovedPlan(planId, {
            ...(selectedChannelName ? { unicommerceChannelName: selectedChannelName } : {}),
        })
            // Swallow transport errors (e.g. a gateway timeout on the long synchronous push): the
            // backend may still have succeeded, so the verdict comes from shipment status, not this promise.
            .catch(() => undefined)
            .then(() => getPlanShipments(planId))
            .then(final => {
                setShipments(final);
                const outcome = reportWmsPushOutcome(final, codes);
                setPhase(outcome === 'error' ? 'diff' : 'done'); // all-failed → back to diff for retry
                onCreated();
            })
            .catch(() => {
                // Only reached if we couldn't even read shipment status back.
                showError('Could not confirm sale-order status. Please refresh.');
                setPhase('diff');
            })
            .finally(() => {
                stopAllPolls();
                setPushingCodes(new Set());
            });
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const pushableShipments = shipments.filter(s => s.wmsStatus === null || s.wmsStatus === 'FAILED');
    const createdShipments  = shipments.filter(s => s.wmsStatus === 'CREATED');
    const wmsLabel     = wmsType ? wmsType.charAt(0).toUpperCase() + wmsType.slice(1) : 'WMS';
    const isInProcess  = phase !== 'idle';
    const hasError     = !!reviewError;

    // Progress bar values during 'creating' phase
    const totalPushCount = pushingCodes.size;
    const createdCount   = [...pushingCodes].filter(code => {
        const s = shipments.find(x => x.saleOrderCode === code);
        return s?.wmsStatus === 'CREATED';
    }).length;

    // Contextual sub-text for each step
    const stepDescriptions: Record<StepKey, string | null> = {
        review: phase === 'reviewing'
            ? 'Checking line quantities against current stock...'
            : preview && (phase === 'diff' || phase === 'creating' || phase === 'done')
            ? preview.overAvailableLineCount > 0
                ? `${preview.overAvailableLineCount} line${preview.overAvailableLineCount !== 1 ? 's' : ''} above available stock — pushed as-is`
                : `All ${preview.lines.length} lines within available stock`
            : null,
        create: phase === 'creating'
            ? `${createdCount} of ${totalPushCount} created`
            : phase === 'done'
            ? `All ${totalPushCount} sale order${totalPushCount !== 1 ? 's' : ''} in WMS · inventory refresh queued`
            : null,
    };

    const toggleExpand = (code: string) => {
        setExpandedCodes(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    if (!opened) return null;

    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
            onClick={e => {
                // Only close by clicking backdrop when idle; block during active phases
                if (e.target === e.currentTarget && phase === 'idle') onClose();
            }}
        >
            <div style={{
                backgroundColor: '#fff', borderRadius: 12,
                width: '100%', maxWidth: 580,
                maxHeight: '88vh', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}>

                {/* ── Header ───────────────────────────────────────────────── */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Text style={{ fontSize: 16, fontWeight: 700, color: neutralColors.textPrimary }}>
                        Push to WMS
                    </Text>
                    <Text style={{ fontSize: 13, color: neutralColors.textSecondary, marginTop: 4 }}>
                        {isInProcess
                            ? 'Checking quantities against current stock, then pushing to WMS at the approved quantities.'
                            : `Creates a sale order in ${wmsLabel} for each pending shipment.`}
                    </Text>
                </div>

                {/* ── Body ─────────────────────────────────────────────────── */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                            <Loader size="sm" />
                        </div>
                    ) : !isInProcess ? (

                        // ── IDLE: pre-creation shipments list ─────────────────
                        <>
                            {shipments.filter(s => s.wmsStatus === 'FAILED').length > 0 && (
                                <div style={{
                                    padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                                    backgroundColor: 'rgba(239,68,68,0.06)',
                                    border: '1px solid rgba(239,68,68,0.2)',
                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                }}>
                                    <Warning size={16} color={semanticColors.critical.primary} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                                    <Text style={{ fontSize: 13, color: neutralColors.textPrimary }}>
                                        <strong>{shipments.filter(s => s.wmsStatus === 'FAILED').length} shipment{shipments.filter(s => s.wmsStatus === 'FAILED').length !== 1 ? 's' : ''}</strong>{' '}
                                        failed previously and will be retried.
                                    </Text>
                                </div>
                            )}
                            <Text style={{ fontSize: 11, fontWeight: 600, color: neutralColors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                                Shipments ({shipments.length})
                            </Text>
                            <ShipmentList
                                shipments={shipments}
                                channelKey={channelKey}
                                pushingCodes={pushingCodes}
                                expandedCodes={expandedCodes}
                                onToggleExpand={toggleExpand}
                                showStatus={false}
                            />
                        </>

                    ) : (

                        // ── PROCESS VIEW: stepper + context content ───────────
                        <div style={{ display: 'flex', flexDirection: 'column' }}>

                            <VerticalStepper
                                phase={phase}
                                hasError={hasError}
                                descriptions={stepDescriptions}
                            />

                            {/* Error banner */}
                            {reviewError && (
                                <div style={{
                                    marginTop: 16, padding: '10px 14px', borderRadius: 8,
                                    backgroundColor: 'rgba(239,68,68,0.06)',
                                    border: '1px solid rgba(239,68,68,0.2)',
                                    display: 'flex', gap: 10, alignItems: 'flex-start',
                                }}>
                                    <XCircle size={15} color={semanticColors.critical.primary} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
                                    <Text style={{ fontSize: 13, color: semanticColors.critical.primary }}>
                                        {reviewError}
                                    </Text>
                                </div>
                            )}

                            {/* Review table — lines above their source's available stock */}
                            {phase === 'diff' && preview && (
                                <div style={{ marginTop: 20 }}>
                                    <DiffSection preview={preview} />
                                </div>
                            )}

                            {/* Progress bar + shipment list — shown during / after creation */}
                            {(phase === 'creating' || phase === 'done') && (
                                <div style={{ marginTop: 20 }}>
                                    <ProgressBar current={createdCount} total={totalPushCount} />
                                    <div style={{ marginTop: 16 }}>
                                        <ShipmentList
                                            shipments={shipments}
                                            channelKey={channelKey}
                                            pushingCodes={pushingCodes}
                                            expandedCodes={expandedCodes}
                                            onToggleExpand={toggleExpand}
                                            showStatus
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ───────────────────────────────────────────────── */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {/* WMS channel selector (idle only) */}
                    {!isInProcess && !loading && wmsChannelNames.length > 0 && (
                        <Select
                            label={`${wmsLabel} channel`}
                            placeholder="Select channel"
                            size="sm"
                            data={wmsChannelNames.map(n => ({ value: n, label: n }))}
                            value={selectedChannelName}
                            onChange={setSelectedChannelName}
                            searchable
                            comboboxProps={{ withinPortal: true, zIndex: 10000 }}
                        />
                    )}

                    <Group justify="flex-end" gap={8}>

                        {/* Cancel / Close (hidden while a step is actively running) */}
                        {phase !== 'reviewing' && phase !== 'creating' && (
                            <Button
                                variant="subtle" color="dark" size="sm"
                                onClick={onClose}
                            >
                                {phase === 'done' ? 'Close' : 'Cancel'}
                            </Button>
                        )}

                        {/* Retry after error */}
                        {reviewError && (
                            <Button
                                size="sm" color="red" variant="light"
                                onClick={() => { setReviewError(null); setPhase('idle'); }}
                            >
                                Try again
                            </Button>
                        )}

                        {/* Idle: start the flow */}
                        {phase === 'idle' && (
                            <Button
                                size="sm"
                                color="violet"
                                disabled={loading || pushableShipments.length === 0 || (wmsChannelNames.length > 0 && !selectedChannelName)}
                                onClick={handleStartCreation}
                            >
                                {pushableShipments.length > 0
                                    ? `Create ${pushableShipments.length} sale order${pushableShipments.length !== 1 ? 's' : ''}`
                                    : `${createdShipments.length} already in WMS`
                                }
                            </Button>
                        )}

                        {/* Diff: user confirms and triggers WMS push */}
                        {phase === 'diff' && !reviewError && (
                            <Button size="sm" color="teal" onClick={handleConfirmCreate}>
                                Confirm & push to WMS
                            </Button>
                        )}
                    </Group>
                </div>
            </div>
        </div>,
        document.body,
    );
}
